import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ViewModeProvider } from "@/contexts/ViewModeContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
import { HelpCenterButton } from "@/components/support/HelpCenterButton";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { NotificationPrompt } from "@/components/pwa/NotificationPrompt";
import { OfflineSyncIndicator } from "@/components/pwa/OfflineSyncIndicator";
import { EmailNurturingManager } from "@/components/EmailNurturingManager";
import { VoiceInterfaceBoundary } from "@/components/voice/VoiceInterfaceBoundary";
import { FeatureAdoptionTracker } from "@/components/analytics/FeatureAdoptionTracker";
import { AccessibilityProvider } from "@/components/accessibility/AccessibilityProvider";
import { AccessibilityPanel } from "@/components/accessibility/AccessibilityPanel";
import { InteractiveOnboarding } from "@/components/onboarding/InteractiveOnboarding";

// Lazy load pages for code splitting
const Landing = lazy(() => import("@/pages/Landing"));
const Auth = lazy(() => import("@/pages/Auth"));
const Entrada = lazy(() => import("@/pages/Entrada"));
const PainelExecutivo = lazy(() => import("@/pages/PainelExecutivo"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Planos = lazy(() => import("@/pages/Planos"));
const Diagnostico = lazy(() => import("@/pages/Diagnostico"));
const Relatorio = lazy(() => import("@/pages/Relatorio"));
const ParaContadores = lazy(() => import("@/pages/ParaContadores"));
const ParceriaContadores = lazy(() => import("@/pages/ParceriaContadores"));
const Contadores = lazy(() => import("@/pages/Contadores"));
const ContadorCadastro = lazy(() => import("@/pages/ContadorCadastro"));
const ContadorLogin = lazy(() => import("@/pages/ContadorLogin"));
const ContadorDashboard = lazy(() => import("@/pages/ContadorDashboard"));
const ContadorSucesso = lazy(() => import("@/pages/ContadorSucesso"));
const BoasVindasIndicado = lazy(() => import("@/pages/BoasVindasIndicado"));
const RelatorioPublico = lazy(() => import("@/pages/RelatorioPublico"));
const TermoParceria = lazy(() => import("@/pages/TermoParceria"));
const ComoFunciona = lazy(() => import("@/pages/ComoFunciona"));
const Cardapio = lazy(() => import("@/pages/Cardapio"));
const Inteligencia = lazy(() => import("@/pages/Inteligencia"));
const Ajuda = lazy(() => import("@/pages/Ajuda"));
const AdminSuporteInterno = lazy(() => import("@/pages/AdminSuporteInterno"));
const Instalar = lazy(() => import("@/pages/Instalar"));
const Integracoes = lazy(() => import("@/pages/Integracoes"));
const Changelog = lazy(() => import("@/pages/Changelog"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const TermosDeUso = lazy(() => import("@/pages/TermosDeUso"));
const RelatoriosExecutivos = lazy(() => import("@/pages/RelatoriosExecutivos"));
const RadarSentinela = lazy(() => import("@/pages/RadarSentinela"));
const RadarPublico = lazy(() => import("@/pages/RadarPublico"));
const NotFound = lazy(() => import("@/pages/NotFound"));

const queryClient = new QueryClient();

// Premium loading fallback with skeleton animation
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="relative">
        <div className="h-12 w-12 rounded-full border-4 border-muted animate-spin border-t-primary" />
      </div>
      <p className="text-sm text-muted-foreground animate-pulse">Carregando...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <ViewModeProvider>
              <AccessibilityProvider>
                {/* Skip to main content link for keyboard users */}
                <a href="#main-content" className="skip-to-main">
                  Ir para o conteúdo principal
                </a>
                
                <Toaster />
                <Sonner />
                <ScrollToTop />
                <HelpCenterButton />
                <InstallPrompt />
                <NotificationPrompt />
                <OfflineSyncIndicator />
                <EmailNurturingManager />
                <VoiceInterfaceBoundary />
                <AccessibilityPanel />
                <InteractiveOnboarding />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/planos" element={<Planos />} />
                  <Route path="/como-funciona" element={<ComoFunciona />} />
                  <Route path="/instalar" element={<Instalar />} />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/radar-publico" element={<RadarPublico />} />
                  <Route path="/termos" element={<TermosDeUso />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/para-contadores" element={<ParaContadores />} />
                  <Route path="/parceria-contadores" element={<ParceriaContadores />} />
                  <Route path="/contadores" element={<Contadores />} />
                  <Route path="/contador/cadastro" element={<ContadorCadastro />} />
                  <Route path="/contador/login" element={<ContadorLogin />} />
                  <Route path="/contador/sucesso" element={<ContadorSucesso />} />
                  <Route path="/contador/dashboard" element={<ContadorDashboard />} />
                  <Route path="/contador/termo-parceria" element={<TermoParceria />} />
                  <Route path="/boas-vindas-indicado" element={<BoasVindasIndicado />} />
                  <Route path="/relatorio-publico/:id" element={<RelatorioPublico />} />
                  <Route
                    path="/entrada"
                    element={
                      <ProtectedRoute>
                        <Entrada />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/painel-executivo"
                    element={
                      <ProtectedRoute>
                        <PainelExecutivo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/painel"
                    element={
                      <ProtectedRoute>
                        <PainelExecutivo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/diagnostico"
                    element={
                      <ProtectedRoute>
                        <Diagnostico />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/relatorio/:id"
                    element={
                      <ProtectedRoute>
                        <Relatorio />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/cardapio"
                    element={
                      <ProtectedRoute requireSubscription>
                        <Cardapio />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/inteligencia"
                    element={
                      <ProtectedRoute>
                        <Inteligencia />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/radar"
                    element={
                      <ProtectedRoute>
                        <RadarSentinela />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/ajuda"
                    element={
                      <ProtectedRoute>
                        <Ajuda />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/admin/suporte"
                    element={
                      <ProtectedRoute>
                        <AdminSuporteInterno />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/integracoes"
                    element={
                      <ProtectedRoute>
                        <Integracoes />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/relatorios-executivos"
                    element={
                      <ProtectedRoute requirePlan="ultra">
                        <RelatoriosExecutivos />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <FeatureAdoptionTracker />
              </AccessibilityProvider>
            </ViewModeProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
